import { SagaIterator } from "redux-saga";
import { call, select, takeLatest } from "typed-redux-saga";
import { AnyAction } from "redux";
import { Action } from "typescript-fsa";

import {
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { Events, ModelSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { Localizable } from "@com.mgmtp.a12.utils/utils-localization/lib/main";
import { JsonRpc2Response } from "@com.mgmtp.a12.dataservices/dataservices-access";

import { assertNonNullable } from "../../../utilities/assert";
import { getButtonAnnotations } from "../../../utilities/modelHelpers";
import { loadAndMapOpenRegisterRecord, isJsonRpc2ResponseArray } from "../../../utilities/rpcHelper";
import { ModulfDataSelectors } from "../../../utilities/selectors/modulfDataSelectors";
import { RESOURCE_KEYS } from "../../../localization";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { REFRESH_OPENREGISTER_RECORD } from "../../../utilities/variables/customEventNames";
import { executeWithoutValidation } from "../../../utilities/validationUtils";
import { OPENREGISTER_PATH, MAPPING_CONFIGURATION } from "../../../utilities/variables/modulfRuntimeAnnotations";
import { executeBusy, updateWorkflowsActivityData } from "../../../utilities/workflowUtils";

import Exception = JsonRpc2Response.Exception;

const documentService = new DocumentServiceFactory().getDocumentService();

/**
 * Refreshes an already-selected OpenRegister record: re-loads and re-maps it into the
 * Fachverfahren document without requiring the user to pick a record again. The backend
 * resolves the previously stored record ID when no explicit ID is supplied.
 */
export function* refreshOpenRegisterRecordSaga(): SagaIterator<void> {
	yield* takeLatest(
		(action: AnyAction) => {
			return (
				FormEngineActions.event.match(action) &&
				Events.eventButton.match(action.payload.engineEvent) &&
				action.payload.engineEvent.payload.name === REFRESH_OPENREGISTER_RECORD
			);
		},
		function* (action: Action<FormEngineActions.FormEngineEventActions<Action<Events.EventButtonPayload>>>) {
			yield* call(executeBusy, function* () {
				const { activityId } = action.payload;
				yield* call(executeWithoutValidation, activityId, function* () {
					const engineState = yield* select(FormEngineSelectors.engineState(activityId));
					assertNonNullable(engineState);
					const formModel = ModelSelectors.formModel()(engineState);
					const documentModel = ModelSelectors.documentModel()(engineState);
					const { buttonPath } = action.payload.engineEvent.payload;
					const annotations = getButtonAnnotations(formModel, buttonPath);

					const openregisterGroupPath = annotations.find(it => it.name === OPENREGISTER_PATH)?.value;
					const mappingConfigurationId = annotations.find(it => it.name === MAPPING_CONFIGURATION)?.value;

					const data = yield* select(ModulfDataSelectors.selectTaskDocument(activityId));
					if (!(openregisterGroupPath && data && mappingConfigurationId)) {
						return;
					}

					const path = openregisterGroupPath.substring(openregisterGroupPath.indexOf("/root/"));

					try {
						const updatedDocument = yield* call(
							loadAndMapOpenRegisterRecord,
							documentService.formatDates(data, documentModel),
							data.id,
							path,
							mappingConfigurationId
						);

						const parsedDocument = {
							...documentService.parseDates(updatedDocument, documentModel),
							modelId: documentModel.header.id
						};
						yield* call(updateWorkflowsActivityData, parsedDocument);
					} catch (error) {
						let message: Localizable;
						if (isJsonRpc2ResponseArray(error)) {
							message = extractExceptionMessage(error);
						} else {
							message = {
								key: RESOURCE_KEYS.features.notification.error.unexpectedError
							};
						}
						yield* call(
							putErrorNotification,
							message,
							error,
							action.payload.activityId,
							"openregisterSelection"
						);
					}
				});
			});
		}
	);
}

function extractExceptionMessage(response: JsonRpc2Response[]): Localizable {
	const description = response
		.filter(JsonRpc2Response.error.isInstance)
		.map(it => it.error.data)
		.filter(Exception.isInstance)
		.map(it => it.description)
		.pop();
	return {
		key: RESOURCE_KEYS.features.notification.error.unexpectedError,
		args: { reason: { type: "plain", value: description?.default ?? "Unbekannter Fehler." } }
	};
}
